"use client"

import * as React from "react"
import { XIcon } from "lucide-react"
import { Dialog as DialogPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { vieneDeOtraCapa } from "@/components/ui/capas"

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        // `dd-backdrop`: viñeta radial + desenfoque, en vez del plano negro al
        // 50%. La entrada la maneja la keyframe de `index.css` (220ms), por eso
        // acá queda solo la salida.
        "dd-backdrop fixed inset-0 z-50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          // max-h + scroll propio: sin esto, un diálogo más alto que la
          // ventana desborda y sus botones quedan FUERA de alcance — el click
          // cae en el fondo y lo cierra sin hacer nada. Le pasaba al alta de
          // partner en una ventana de ~670px de alto.
          // `grid-cols-[minmax(0,1fr)]`: la columna implícita de un grid es
          // `auto`, o sea que se dimensiona por el MIN-CONTENT de lo que tenga
          // adentro — y el min-content de un texto `white-space: nowrap` es el
          // string entero. Con eso, un solo valor largo sin espacios estira la
          // columna más allá del ancho del diálogo y el contenido se sale por
          // la derecha; encima `overflow-y-auto` obliga a `overflow-x: auto`,
          // así que aparece una barra horizontal y el resto queda cortado.
          // `truncate` no lo salva: recorta cuando el ancho YA está definido,
          // no lo define. Pasó en "Facturar la cuota" con el título del
          // contrato. Con `minmax(0, …)` la columna es el ancho del diálogo y
          // recién ahí `truncate` hace su trabajo.
          // `dd-float dd-float-head`: vidrio, borde con gradiente, sombra con
          // filete, velo de marca y línea luminosa en el tope del contenido.
          //
          // 🔴 NO se agrega `overflow-hidden` (el handoff lo pedía para que los
          // pseudo-elementos respetaran el radio): `overflow-y-auto` YA recorta
          // contra el borde redondeado —cualquier `overflow` distinto de
          // `visible` lo hace—, y clipear acá le sacaría el desplazamiento
          // interno al diálogo, que existe por un bug real: sin él, un diálogo
          // más alto que la ventana deja sus botones fuera de alcance.
          //
          // La entrada sale de la keyframe `dd-dialog-in` de `index.css`, no de
          // `animate-in zoom-in-95`: tw-animate escribe `animation:` en el
          // mismo elemento y una de las dos le gana a la otra. La salida se
          // conserva.
          "dd-float dd-float-head fixed top-[50%] left-[50%] z-50 grid grid-cols-[minmax(0,1fr)] max-h-[90dvh] w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto rounded-dialog p-6 duration-200 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 sm:max-w-lg",
          className
        )}
        {...props}
        // Un clic en una confirmación que estaba encima no es un clic afuera
        // de este diálogo (ver `capas.ts`): sin esto, cancelar la pregunta de
        // cierre volvía a pedir el cierre y el diálogo quedaba trabado.
        onInteractOutside={(event) => {
          onInteractOutside?.(event)
          if (!event.defaultPrevented && vieneDeOtraCapa(event.target)) event.preventDefault()
        }}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            // Se lee como un botón de ícono y no como una X flotante: caja de
            // 28px, radio del sistema y color propio en vez de `opacity-70`.
            className="text-fg-dim absolute top-3.5 right-3.5 grid size-7 place-items-center rounded-md transition-colors hover:bg-fg/8 hover:text-fg focus:ring-2 focus:ring-ring focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

/**
 * Encabezado del diálogo.
 *
 * 🔴 Alineado a la izquierda SIEMPRE. El default de shadcn es
 * `text-center sm:text-left`, o sea que en un teléfono —y solo ahí— el título y
 * la bajada quedan centrados mientras el formulario de abajo sigue alineado a
 * la izquierda: dos ejes de lectura en la misma tarjeta. En un diálogo con
 * avatar o ícono a la izquierda queda directamente roto. Si algún diálogo
 * puntual quiere el texto centrado, que lo pida con `className`.
 */
function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-left", className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-heading leading-none font-semibold", className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-body-lg text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
